import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { dmyTime } from "@/lib/format";

export default function Notifications() {
  const { can, user } = useAuth();
  const [items, setItems] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      try {
        const wa = await api.get("/whatsapp/messages");
        setMessages(wa.data.items);
      } catch { /* permission-gated */ }
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { if (can("notifications.view")) load(); }, [load, can]);
  if (!can("notifications.view")) return <NoAccess what="notifications" />;
  if (error) return <ErrorState message={error} />;
  if (!items) return <Loading />;

  const markRead = async (n) => {
    await api.post(`/notifications/${n.notification_id}/read`);
    load();
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader overline="Administration" title="Notification centre"
        subtitle="Operational alerts for your business plus the delivery log of every WhatsApp message." />

      <div className="panel mb-10">
        <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">In-app notifications</h3></div>
        {items.length === 0 ? <EmptyState title="No notifications" subtitle="Alerts appear here as your team works." testid="notifications-empty" /> : (
          <div className="divide-y divide-slate-100">
            {items.map((n) => {
              const unread = !(n.read_by || []).includes(user.user_id);
              return (
                <div key={n.notification_id} className={`px-6 py-4 flex items-start justify-between gap-4 ${unread ? "bg-slate-50/70" : ""}`}
                  data-testid={`notification-${n.notification_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{n.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{n.body}</p>
                    <p className="text-[11px] text-slate-400 mt-1.5">{dmyTime(n.created_at)} · {n.module}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {n.link && <Link to={n.link} className={ghostBtnCls} data-testid={`open-notification-${n.notification_id}`}>Open</Link>}
                    {unread && <button onClick={() => markRead(n)} className={ghostBtnCls} data-testid={`read-notification-${n.notification_id}`}>Mark read</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="overline text-[#EA580C] mb-4">WhatsApp delivery log</p>
      <Table testid="whatsapp-table" columns={["When", "Type", "Recipient", "Status", "Provider ID / error"]}
        rows={messages} empty={<EmptyState title="No WhatsApp messages yet"
          subtitle="Messages are logged here with their delivery status once credentials are configured." testid="whatsapp-empty" />}
        renderRow={(m) => (
          <tr key={m.message_id} data-testid={`whatsapp-row-${m.message_id}`}>
            <Td className="text-slate-500 whitespace-nowrap">{dmyTime(m.created_at)}</Td>
            <Td className="text-slate-700">{String(m.message_type || m.kind || m.template || "—").replace(/_/g, " ")}</Td>
            <Td className="font-mono-num text-slate-600">{m.recipient ? `+${m.recipient}` : (m.to || "—")}</Td>
            <Td><StatusPill status={m.status === "sent" ? "completed" : (m.status || "pending")} /></Td>
            <Td className="text-xs text-slate-500 max-w-[280px] truncate">{m.provider_message_id || m.error || "—"}</Td>
          </tr>
        )} />
    </div>
  );
}
